/**
 * Tiny replacement for the `ms` npm package. Mirrors the public surface of
 * https://github.com/vercel/ms — exposes `ms`, `parse`, `parseStrict`, and
 * `format` with the same semantics.
 */

const s = 1000
const m = s * 60
const h = m * 60
const d = h * 24
const w = d * 7
const y = d * 365.25
const mo = y / 12

type Years = "years" | "year" | "yrs" | "yr" | "y"
type Months = "months" | "month" | "mo"
type Weeks = "weeks" | "week" | "w"
type Days = "days" | "day" | "d"
type Hours = "hours" | "hour" | "hrs" | "hr" | "h"
type Minutes = "minutes" | "minute" | "mins" | "min" | "m"
type Seconds = "seconds" | "second" | "secs" | "sec" | "s"
type Milliseconds = "milliseconds" | "millisecond" | "msecs" | "msec" | "ms"
type Unit =
	| Years
	| Months
	| Weeks
	| Days
	| Hours
	| Minutes
	| Seconds
	| Milliseconds

type UnitAnyCase = Capitalize<Unit> | Uppercase<Unit> | Unit

export type StringValue =
	| `${number}`
	| `${number}${UnitAnyCase}`
	| `${number} ${UnitAnyCase}`

interface Options {
	long?: boolean
}

/* oxlint-disable no-redeclare */
export function ms(value: StringValue, options?: Options): number
export function ms(value: number, options?: Options): string
export function ms(
	value: StringValue | number,
	options?: Options,
): number | string {
	/* oxlint-enable no-redeclare */
	if (typeof value === "string") {
		return parse(value)
	} else if (typeof value === "number") {
		return format(value, options)
	}
	throw new Error(
		`Value provided to ms() must be a string or number. value=${JSON.stringify(value)}`,
	)
}

export function parse(str: string): number {
	if (typeof str !== "string" || str.length === 0 || str.length > 100) {
		throw new Error(
			`Value provided to ms.parse() must be a string with length between 1 and 100. value=${JSON.stringify(str)}`,
		)
	}
	const match =
		/^(?<value>-?\d*\.?\d+) *(?<unit>milliseconds?|msecs?|ms|seconds?|secs?|s|minutes?|mins?|m|hours?|hrs?|h|days?|d|weeks?|w|months?|mo|years?|yrs?|y)?$/i.exec(
			str,
		)

	if (!match?.groups) {
		return NaN
	}

	const { value, unit = "ms" } = match.groups as {
		value: string
		unit: string | undefined
	}

	const n = parseFloat(value)
	const matchUnit = unit.toLowerCase() as Lowercase<Unit>

	switch (matchUnit) {
		case "years":
		case "year":
		case "yrs":
		case "yr":
		case "y":
			return n * y
		case "months":
		case "month":
		case "mo":
			return n * mo
		case "weeks":
		case "week":
		case "w":
			return n * w
		case "days":
		case "day":
		case "d":
			return n * d
		case "hours":
		case "hour":
		case "hrs":
		case "hr":
		case "h":
			return n * h
		case "minutes":
		case "minute":
		case "mins":
		case "min":
		case "m":
			return n * m
		case "seconds":
		case "second":
		case "secs":
		case "sec":
		case "s":
			return n * s
		case "milliseconds":
		case "millisecond":
		case "msecs":
		case "msec":
		case "ms":
			return n
		default:
			matchUnit satisfies never
			throw new Error(
				`Unknown unit "${matchUnit}" provided to ms.parse(). value=${JSON.stringify(str)}`,
			)
	}
}

export function parseStrict(value: StringValue): number {
	return parse(value)
}

function fmtShort(value: number): StringValue {
	const msAbs = Math.abs(value)
	if (msAbs >= y) {
		return `${Math.round(value / y)}y`
	}
	if (msAbs >= mo) {
		return `${Math.round(value / mo)}mo`
	}
	if (msAbs >= w) {
		return `${Math.round(value / w)}w`
	}
	if (msAbs >= d) {
		return `${Math.round(value / d)}d`
	}
	if (msAbs >= h) {
		return `${Math.round(value / h)}h`
	}
	if (msAbs >= m) {
		return `${Math.round(value / m)}m`
	}
	if (msAbs >= s) {
		return `${Math.round(value / s)}s`
	}
	return `${value}ms`
}

function fmtLong(value: number): StringValue {
	const msAbs = Math.abs(value)
	if (msAbs >= y) {
		return plural(value, msAbs, y, "year")
	}
	if (msAbs >= mo) {
		return plural(value, msAbs, mo, "month")
	}
	if (msAbs >= w) {
		return plural(value, msAbs, w, "week")
	}
	if (msAbs >= d) {
		return plural(value, msAbs, d, "day")
	}
	if (msAbs >= h) {
		return plural(value, msAbs, h, "hour")
	}
	if (msAbs >= m) {
		return plural(value, msAbs, m, "minute")
	}
	if (msAbs >= s) {
		return plural(value, msAbs, s, "second")
	}
	return `${value} ms`
}

export function format(value: number, options?: Options): string {
	if (typeof value !== "number" || !Number.isFinite(value)) {
		throw new Error("Value provided to ms.format() must be of type number.")
	}
	return options?.long ? fmtLong(value) : fmtShort(value)
}

function plural(
	value: number,
	msAbs: number,
	n: number,
	name: string,
): StringValue {
	const isPlural = msAbs >= n * 1.5
	return `${Math.round(value / n)} ${name}${isPlural ? "s" : ""}` as StringValue
}

export default ms
